"use client"

import React, { useState, useEffect } from "react"
import api from "../config/api"
import { FiPlus, FiEdit2, FiTrash2, FiTrendingUp, FiTrendingDown } from "react-icons/fi"
import "./Finances.css"

const Finances = () => {
    const [finances, setFinances] = useState([])
    const [customers, setCustomers] = useState([])
    const [services, setServices] = useState([])
    const [departments, setDepartments] = useState([])
    const [users, setUsers] = useState([]) // Added users state
    const [summary, setSummary] = useState(null)
    const [loading, setLoading] = useState(true)
    const [showModal, setShowModal] = useState(false)
    const [selectedFinance, setSelectedFinance] = useState(null)
    const [filters, setFilters] = useState({
        type: "",
        start_date: "",
        end_date: "",
    })
    const [formData, setFormData] = useState({
        type: "income",
        amount: "",
        description: "",
        transaction_date: new Date().toISOString().split("T")[0],
        customer_id: "",
        service_id: "",
        department_id: "",
        user_id: "", // Added user_id to formData
    })
    const [error, setError] = useState("")

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        try {
            const [financesRes, customersRes, servicesRes, deptsRes, usersRes, summaryRes] = await Promise.all([
                api.get("/finances"),
                api.get("/customers"),
                api.get("/services"),
                api.get("/departments"),
                api.get("/users"),
                api.get("/finances/summary/data"),
            ])
            setFinances(financesRes.data.data || financesRes.data)
            setCustomers(customersRes.data.data || customersRes.data)
            setServices(servicesRes.data)
            setDepartments(deptsRes.data)
            setUsers(usersRes.data.data || usersRes.data) // Set users
            setSummary(summaryRes.data)
        } catch (error) {
            console.error("Veri yüklenemedi:", error)
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError("")

        try {
            if (selectedFinance) {
                await api.put(`/finances/${selectedFinance.id}`, formData)
            } else {
                await api.post("/finances", formData)
            }
            setShowModal(false)
            resetForm()
            loadData()
        } catch (error) {
            setError(error.response?.data?.message || "İşlem başarısız")
        }
    }

    const handleDelete = async (id) => {
        if (!window.confirm("Bu kaydı silmek istediğinizden emin misiniz?")) return

        try {
            await api.delete(`/finances/${id}`)
            loadData()
        } catch (error) {
            alert("Silme işlemi başarısız")
        }
    }

    const handleEdit = (finance) => {
        setSelectedFinance(finance)
        setFormData({
            type: finance.type,
            amount: finance.amount,
            description: finance.description || "",
            transaction_date: finance.transaction_date,
            customer_id: finance.customer_id || "",
            service_id: finance.service_id || "",
            department_id: finance.department_id || "",
            user_id: finance.user_id || "", // Added user_id
        })
        setShowModal(true)
    }

    const resetForm = () => {
        setFormData({
            type: "income",
            amount: "",
            description: "",
            transaction_date: new Date().toISOString().split("T")[0],
            customer_id: "",
            service_id: "",
            department_id: "",
            user_id: "", // Added user_id
        })
        setSelectedFinance(null)
        setError("")
    }

    const filteredFinances = finances.filter((finance) => {
        const matchesType = !filters.type || finance.type === filters.type
        const matchesStartDate = !filters.start_date || finance.transaction_date >= filters.start_date
        const matchesEndDate = !filters.end_date || finance.transaction_date <= filters.end_date
        return matchesType && matchesStartDate && matchesEndDate
    })

    if (loading) {
        return React.createElement("div", { className: "loading" }, React.createElement("div", { className: "spinner" }))
    }

    return React.createElement(
        "div",
        { className: "finances-page" },
        React.createElement(
            "div",
            { className: "page-header" },
            React.createElement("h1", null, "Gelir / Gider Yönetimi"),
            React.createElement(
                "button",
                {
                    className: "btn btn-primary",
                    onClick: () => {
                        resetForm()
                        setShowModal(true)
                    },
                },
                React.createElement(FiPlus),
                "Yeni Kayıt",
            ),
        ),
        summary &&
        React.createElement(
            "div",
            { className: "summary-cards" },
            React.createElement(
                "div",
                { className: "summary-card income" },
                React.createElement(FiTrendingUp, { className: "summary-icon" }),
                React.createElement(
                    "div",
                    null,
                    React.createElement("h3", null, "Toplam Gelir"),
                    React.createElement("p", null, `₺${summary.total_income?.toLocaleString() || 0}`),
                ),
            ),
            React.createElement(
                "div",
                { className: "summary-card expense" },
                React.createElement(FiTrendingDown, { className: "summary-icon" }),
                React.createElement(
                    "div",
                    null,
                    React.createElement("h3", null, "Toplam Gider"),
                    React.createElement("p", null, `₺${summary.total_expense?.toLocaleString() || 0}`),
                ),
            ),
            React.createElement(
                "div",
                { className: `summary-card profit ${summary.net_profit >= 0 ? "positive" : "negative"}` },
                React.createElement(FiTrendingUp, { className: "summary-icon" }),
                React.createElement(
                    "div",
                    null,
                    React.createElement("h3", null, "Net Kar"),
                    React.createElement("p", null, `₺${summary.net_profit?.toLocaleString() || 0}`),
                ),
            ),
        ),
        React.createElement(
            "div",
            { className: "card filters-card" },
            React.createElement(
                "div",
                { className: "filters" },
                React.createElement(
                    "select",
                    {
                        value: filters.type,
                        onChange: (e) => setFilters({ ...filters, type: e.target.value }),
                    },
                    React.createElement("option", { value: "" }, "Tüm İşlemler"),
                    React.createElement("option", { value: "income" }, "Gelir"),
                    React.createElement("option", { value: "expense" }, "Gider"),
                ),
                React.createElement("input", {
                    type: "date",
                    value: filters.start_date,
                    onChange: (e) => setFilters({ ...filters, start_date: e.target.value }),
                    placeholder: "Başlangıç Tarihi",
                }),
                React.createElement("input", {
                    type: "date",
                    value: filters.end_date,
                    onChange: (e) => setFilters({ ...filters, end_date: e.target.value }),
                    placeholder: "Bitiş Tarihi",
                }),
            ),
        ),
        React.createElement(
            "div",
            { className: "desktop-table" },
            React.createElement(
                "div",
                { className: "card" },
                React.createElement(
                    "div",
                    { className: "table-container" },
                    React.createElement(
                        "table",
                        null,
                        React.createElement(
                            "thead",
                            null,
                            React.createElement(
                                "tr",
                                null,
                                React.createElement("th", null, "Tarih"),
                                React.createElement("th", null, "Tür"),
                                React.createElement("th", null, "Açıklama"),
                                React.createElement("th", null, "Tutar"),
                                React.createElement("th", null, "Müşteri"),
                                React.createElement("th", null, "Personel"), // Added Personel column
                                React.createElement("th", null, "İşlemler"),
                            ),
                        ),
                        React.createElement(
                            "tbody",
                            null,
                            filteredFinances.map((finance) =>
                                React.createElement(
                                    "tr",
                                    { key: finance.id },
                                    React.createElement("td", null, new Date(finance.transaction_date).toLocaleDateString("tr-TR")),
                                    React.createElement(
                                        "td",
                                        null,
                                        React.createElement(
                                            "span",
                                            { className: `badge ${finance.type === "income" ? "badge-success" : "badge-danger"}` },
                                            finance.type === "income" ? "Gelir" : "Gider",
                                        ),
                                    ),
                                    React.createElement("td", null, finance.description || "-"),
                                    React.createElement(
                                        "td",
                                        { className: finance.type === "income" ? "text-success" : "text-danger" },
                                        `${finance.type === "income" ? "+" : "-"}₺${finance.amount}`,
                                    ),
                                    React.createElement(
                                        "td",
                                        null,
                                        finance.customer ? `${finance.customer.first_name} ${finance.customer.last_name}` : "-",
                                    ),
                                    React.createElement(
                                        "td",
                                        null,
                                        finance.user ? `${finance.user.first_name} ${finance.user.last_name}` : "-",
                                    ),
                                    React.createElement(
                                        "td",
                                        null,
                                        React.createElement(
                                            "div",
                                            { className: "action-buttons" },
                                            React.createElement(
                                                "button",
                                                { className: "btn-icon", onClick: () => handleEdit(finance) },
                                                React.createElement(FiEdit2),
                                            ),
                                            React.createElement(
                                                "button",
                                                { className: "btn-icon danger", onClick: () => handleDelete(finance.id) },
                                                React.createElement(FiTrash2),
                                            ),
                                        ),
                                    ),
                                ),
                            ),
                        ),
                    ),
                ),
            ),
        ),
        React.createElement(
            "div",
            { className: "mobile-cards" },
            filteredFinances.map((finance) =>
                React.createElement(
                    "div",
                    { key: finance.id, className: "mobile-card" },
                    React.createElement(
                        "div",
                        { className: "mobile-card-header" },
                        React.createElement(
                            "span",
                            { className: `badge ${finance.type === "income" ? "badge-success" : "badge-danger"}` },
                            finance.type === "income" ? "Gelir" : "Gider",
                        ),
                        React.createElement(
                            "span",
                            { className: `mobile-card-amount ${finance.type === "income" ? "text-success" : "text-danger"}` },
                            `${finance.type === "income" ? "+" : "-"}₺${finance.amount}`,
                        ),
                    ),
                    React.createElement(
                        "div",
                        { className: "mobile-card-body" },
                        React.createElement(
                            "div",
                            { className: "mobile-card-row" },
                            React.createElement("span", { className: "mobile-card-label" }, "Tarih:"),
                            React.createElement("span", null, new Date(finance.transaction_date).toLocaleDateString("tr-TR")),
                        ),
                        finance.description &&
                        React.createElement(
                            "div",
                            { className: "mobile-card-row" },
                            React.createElement("span", { className: "mobile-card-label" }, "Açıklama:"),
                            React.createElement("span", null, finance.description),
                        ),
                        finance.customer &&
                        React.createElement(
                            "div",
                            { className: "mobile-card-row" },
                            React.createElement("span", { className: "mobile-card-label" }, "Müşteri:"),
                            React.createElement("span", null, `${finance.customer.first_name} ${finance.customer.last_name}`),
                        ),
                        finance.user &&
                        React.createElement(
                            "div",
                            { className: "mobile-card-row" },
                            React.createElement("span", { className: "mobile-card-label" }, "Personel:"),
                            React.createElement("span", null, `${finance.user.first_name} ${finance.user.last_name}`),
                        ),
                    ),
                    React.createElement(
                        "div",
                        { className: "mobile-card-actions" },
                        React.createElement(
                            "button",
                            { className: "btn-icon", onClick: () => handleEdit(finance) },
                            React.createElement(FiEdit2),
                        ),
                        React.createElement(
                            "button",
                            { className: "btn-icon danger", onClick: () => handleDelete(finance.id) },
                            React.createElement(FiTrash2),
                        ),
                    ),
                ),
            ),
        ),
        showModal &&
        React.createElement(
            "div",
            { className: "modal-overlay", onClick: () => setShowModal(false) },
            React.createElement(
                "div",
                { className: "modal", onClick: (e) => e.stopPropagation() },
                React.createElement("h2", null, selectedFinance ? "Kayıt Düzenle" : "Yeni Kayıt"),
                error && React.createElement("div", { className: "error-message" }, error),
                React.createElement(
                    "form",
                    { onSubmit: handleSubmit },
                    React.createElement(
                        "div",
                        { className: "form-row" },
                        React.createElement(
                            "div",
                            { className: "input-group" },
                            React.createElement("label", null, "Tür *"),
                            React.createElement(
                                "select",
                                {
                                    value: formData.type,
                                    onChange: (e) => setFormData({ ...formData, type: e.target.value }),
                                    required: true,
                                },
                                React.createElement("option", { value: "income" }, "Gelir"),
                                React.createElement("option", { value: "expense" }, "Gider"),
                            ),
                        ),
                        React.createElement(
                            "div",
                            { className: "input-group" },
                            React.createElement("label", null, "Tutar *"),
                            React.createElement("input", {
                                type: "number",
                                step: "0.01",
                                value: formData.amount,
                                onChange: (e) => setFormData({ ...formData, amount: e.target.value }),
                                required: true,
                            }),
                        ),
                    ),
                    React.createElement(
                        "div",
                        { className: "input-group" },
                        React.createElement("label", null, "Tarih *"),
                        React.createElement("input", {
                            type: "date",
                            value: formData.transaction_date,
                            onChange: (e) => setFormData({ ...formData, transaction_date: e.target.value }),
                            required: true,
                        }),
                    ),
                    React.createElement(
                        "div",
                        { className: "input-group" },
                        React.createElement("label", null, "Açıklama"),
                        React.createElement("textarea", {
                            value: formData.description,
                            onChange: (e) => setFormData({ ...formData, description: e.target.value }),
                        }),
                    ),
                    React.createElement(
                        "div",
                        { className: "form-row" },
                        React.createElement(
                            "div",
                            { className: "input-group" },
                            React.createElement("label", null, "Müşteri"),
                            React.createElement(
                                "select",
                                {
                                    value: formData.customer_id,
                                    onChange: (e) => setFormData({ ...formData, customer_id: e.target.value }),
                                },
                                React.createElement("option", { value: "" }, "Seçiniz"),
                                customers.map((customer) =>
                                    React.createElement(
                                        "option",
                                        { key: customer.id, value: customer.id },
                                        `${customer.first_name} ${customer.last_name}`,
                                    ),
                                ),
                            ),
                        ),
                        React.createElement(
                            "div",
                            { className: "input-group" },
                            React.createElement("label", null, "Hizmet"),
                            React.createElement(
                                "select",
                                {
                                    value: formData.service_id,
                                    onChange: (e) => setFormData({ ...formData, service_id: e.target.value }),
                                },
                                React.createElement("option", { value: "" }, "Seçiniz"),
                                services.map((service) =>
                                    React.createElement("option", { key: service.id, value: service.id }, service.name),
                                ),
                            ),
                        ),
                    ),
                    React.createElement(
                        "div",
                        { className: "form-row" },
                        React.createElement(
                            "div",
                            { className: "input-group" },
                            React.createElement("label", null, "Departman"),
                            React.createElement(
                                "select",
                                {
                                    value: formData.department_id,
                                    onChange: (e) => setFormData({ ...formData, department_id: e.target.value }),
                                },
                                React.createElement("option", { value: "" }, "Seçiniz"),
                                departments.map((dept) => React.createElement("option", { key: dept.id, value: dept.id }, dept.name)),
                            ),
                        ),
                        React.createElement(
                            "div",
                            { className: "input-group" },
                            React.createElement("label", null, "Personel"),
                            React.createElement(
                                "select",
                                {
                                    value: formData.user_id,
                                    onChange: (e) => setFormData({ ...formData, user_id: e.target.value }),
                                },
                                React.createElement("option", { value: "" }, "Seçiniz"),
                                users.map((user) =>
                                    React.createElement(
                                        "option",
                                        { key: user.id, value: user.id },
                                        `${user.first_name} ${user.last_name}`,
                                    ),
                                ),
                            ),
                        ),
                    ),
                    React.createElement(
                        "div",
                        { className: "modal-actions" },
                        React.createElement(
                            "button",
                            { type: "button", className: "btn btn-secondary", onClick: () => setShowModal(false) },
                            "İptal",
                        ),
                        React.createElement("button", { type: "submit", className: "btn btn-primary" }, "Kaydet"),
                    ),
                ),
            ),
        ),
    )
}

export default Finances
